import { useCallback, useEffect, useRef, useState } from "react";
import {
  addManualReview,
  listCodeReviewQueue,
  removeReview,
  type CodeReviewItem,
  type CodeReviewQueueResponse,
} from "../api/codeReview";
import { useMe } from "../hooks/useMe";
import { parseTs, fmtAgo, fmtDur, stageLabel, TERMINAL_STATES } from "../lib/codeReviewFormat";

type Props = { project: string; active: boolean };

function fmtUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function CodeReviewRow({
  item,
  project,
  isAdmin,
  refresh,
}: {
  item: CodeReviewItem;
  project: string;
  isAdmin: boolean;
  refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const prUrl = `https://github.com/${item.repo}/pull/${item.pr_number}`;
  const isActive = !TERMINAL_STATES.has(item.state);
  const stageSince = item.stage_since ?? item.updated_at;
  const steps = item.steps ?? [];
  const attempts = item.attempts ?? 0;

  const onRemove = useCallback(async () => {
    setBusy(true);
    setRowError(null);
    try {
      await removeReview(project, item.id);
      await refresh();
    } catch (e) {
      setRowError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [project, item.id, refresh]);

  return (
    <li className="qa-issue cr-row">
      <div className="qa-issue-head">
        <span className={`qa-badge cr-badge-${item.source}`}>
          {item.source === "manual" ? "★ Manual" : "Auto"}
        </span>
        <span className="cr-state" title={`raw state: ${item.state}`}>{stageLabel(item.state)}</span>
        <span className="cr-position">#{item.position}</span>
        <a className="cr-pr-link" href={prUrl} target="_blank" rel="noopener noreferrer">
          {item.repo} #{item.pr_number}
        </a>
        <span className="qa-issue-title">{item.pr_title}</span>
        {isAdmin && typeof item.cost_usd === "number" && (
          <span className="cr-cost" title="spend so far on this review">{fmtUsd(item.cost_usd)}</span>
        )}
        <span className="qa-issue-age">{fmtAgo(item.updated_at)}</span>
        {isAdmin && (
          <button
            type="button"
            className="cr-remove"
            disabled={busy}
            title="Remove from queue"
            aria-label={`Remove PR #${item.pr_number} from the review queue`}
            onClick={() => void onRemove()}
          >
            ×
          </button>
        )}
      </div>
      {(isActive || attempts > 0 || item.fix_rounds > 0 || item.qa_cycles > 0 || item.head_branch) && (
        <div className="cr-meta">
          {isActive && <span className="cr-instep">in this step {fmtDur(Date.now() - parseTs(stageSince))}</span>}
          {attempts > 0 && <span>attempt {attempts + 1}</span>}
          {item.fix_rounds > 0 && <span>fix rounds {item.fix_rounds}</span>}
          {item.qa_cycles > 0 && <span>QA cycles {item.qa_cycles}</span>}
          {item.head_branch && <span className="cr-branch"><code>{item.head_branch}</code></span>}
        </div>
      )}
      {steps.length > 0 && (
        <div className="cr-meta cr-timeline" aria-label="pipeline steps">
          {steps.map((st, i) => {
            const start = parseTs(st.at);
            const end = i + 1 < steps.length ? parseTs(steps[i + 1]!.at) : Date.now();
            const current = i === steps.length - 1 && isActive;
            return (
              <span key={`${st.state}-${st.at}-${i}`} className={current ? "cr-step-current" : "cr-step"}>
                {stageLabel(st.state)} · {fmtDur(end - start)}{current ? " · now" : ""}
              </span>
            );
          })}
        </div>
      )}
      {item.error && <div className="cr-error-line" role="alert">{item.error}</div>}
      {rowError && <div className="cr-error-line" role="alert">{rowError}</div>}
    </li>
  );
}

export default function CodeReviewPanel({ project, active }: Props) {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const [data, setData] = useState<CodeReviewQueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prInput, setPrInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      setData(await listCodeReviewQueue(project));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [project]);

  // Poll while the tab is open so the queue advances live during reviews.
  useEffect(() => {
    if (!active) return;
    void refresh();
    timer.current = setInterval(() => void refresh(), 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [active, refresh]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (adding) return; // idempotency: ignore double-submit while in flight
      const n = Number(prInput);
      if (!Number.isInteger(n) || n <= 0) {
        setAddError("Enter a PR number");
        return;
      }
      setAdding(true);
      setAddError(null);
      try {
        const res = await addManualReview(project, n);
        if (!res.ok) {
          setAddError(res.configured === false ? "Code review not connected" : res.error ?? "could not queue");
          return;
        }
        setPrInput("");
        await refresh();
      } catch (e) {
        setAddError((e as Error).message);
      } finally {
        setAdding(false);
      }
    },
    [adding, prInput, project, refresh],
  );

  if (error) {
    return (
      <div className="qa-panel">
        <div className="error-banner" role="alert">
          <span>Could not load code review queue: {error}</span>
          <button type="button" onClick={() => void refresh()}>Retry</button>
        </div>
      </div>
    );
  }

  if (data === null) return <div className="qa-panel"><p className="placeholder">Loading…</p></div>;

  if (!data.configured) {
    return (
      <div className="qa-panel">
        <p className="placeholder">Code review is not connected to this dashboard yet.</p>
      </div>
    );
  }

  if (data.error || typeof data.upstream === "number") {
    return (
      <div className="qa-panel">
        <p className="placeholder">Code review queue is unavailable right now. Retrying…</p>
      </div>
    );
  }

  const items = data.items;

  return (
    <div className="qa-panel">
      <div className="qa-panel-head">
        <span className="qa-count">
          {items.length === 0 ? "Queue empty" : `${items.length} in queue`}
        </span>
        <span className={`cr-worker ${data.worker_enabled ? "cr-worker-on" : "cr-worker-off"}`}>
          {data.worker_enabled ? "worker active" : "worker paused — queued items wait"}
        </span>
      </div>

      {isAdmin && (
        <form className="cr-add" onSubmit={(e) => void submit(e)} aria-label="Add manual review">
          <label className="cr-add-label">
            PR #
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              placeholder="123"
              value={prInput}
              onChange={(e) => setPrInput(e.target.value)}
              disabled={adding}
              className="cr-add-input"
              aria-label="Pull request number"
            />
          </label>
          <button type="submit" className="cr-add-btn" disabled={adding || prInput.trim().length === 0}>
            {adding ? "Adding…" : "Add"}
          </button>
          {addError && <span className="cr-add-error" role="alert">{addError}</span>}
        </form>
      )}

      {items.length === 0 ? (
        <p className="placeholder">Nothing queued. New pull requests are reviewed automatically.</p>
      ) : (
        <ul className="qa-issue-list">
          {items.map((it) => (
            <CodeReviewRow key={it.id} item={it} project={project} isAdmin={isAdmin} refresh={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
